import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import * as api from '@/lib/api-client';
import { setLocale } from '@/lib/i18n';
import {
  clearSessionStarted,
  isSessionExpired,
  markSessionStarted,
} from '@/lib/session-lifetime';

const STORAGE_KEY = 'h3eromap.session';

/**
 * What we persist across page reloads. Crucially: NO access token. The
 * server sets HttpOnly + Secure + __Host- cookies during login, and
 * every API call rides those cookies via credentials:include. The
 * stored `user` is identity-only — used to render the UI immediately
 * on boot before the first /users/me round-trip confirms the cookie
 * is still valid. Closes CodeFix-V1 #33 (XSS exfil of localStorage
 * token).
 */
interface PersistedSession {
  user: api.UserProfile;
}

interface InMemorySession {
  /** Legacy in-memory access token, kept for callers that still pass it
   *  through. Empty string means "no token; cookie auth only". */
  accessToken: string;
  user: api.UserProfile;
}

/**
 * Read result includes a legacy access token only when the user's
 * localStorage was written by an older build that still persisted
 * tokens. New writes never include the token (see writePersistedSession).
 * Tests pre-seed localStorage with `accessToken` to drive fixtures —
 * honour it on read for back-compat, but stop persisting it forward.
 */
function readPersistedSession(): { user: api.UserProfile; legacyToken: string } | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed !== null && typeof parsed === 'object' && 'user' in parsed) {
      const session = parsed as { user?: unknown; accessToken?: unknown };
      if (session.user && typeof session.user === 'object') {
        const legacyToken =
          typeof session.accessToken === 'string' ? session.accessToken : '';
        return { user: session.user as api.UserProfile, legacyToken };
      }
    }
  } catch {
    /* fall through */
  }
  return null;
}

function writePersistedSession(session: PersistedSession | null): void {
  if (typeof window === 'undefined') return;
  if (session) {
    // Explicitly stripped of any access/refresh token — only the user
    // identity is persisted. Cookies do the actual auth.
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: session.user }));
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

export type SignInResult =
  | { kind: 'session' }
  | { kind: 'mfa_required'; mfaToken: string };

export interface AuthContextValue {
  /** Null when logged out. */
  user: api.UserProfile | null;
  /**
   * Bearer token for `Authorization` headers. After CodeFix V1 #33 the
   * web runs in cookie-only auth mode, so this is the empty string `""`
   * on a cold page load even though the user IS signed in (the HttpOnly
   * `__Host-h3m-access` cookie carries the real auth). Consumers MUST
   * NOT use this as a "signed in" gate — check `user` instead. Pass it
   * through to `apiRequest`; the request helper skips the Authorization
   * header when it's empty.
   */
  accessToken: string;
  /* `this: void` keeps TS-eslint's unbound-method rule quiet — these are
     `useCallback`-stabilized arrow functions, not class methods. */
  signIn: (this: void, email: string, password: string) => Promise<SignInResult>;
  /** USR-006 step 2 — exchange the mfa_token + code for a full session. */
  verifyMfa: (this: void, mfaToken: string, code: string) => Promise<void>;
  signOut: (this: void) => Promise<void>;
  /**
   * #8/#9 — update the signed-in user's saved language in the in-memory +
   * persisted session. Callers should also PATCH the server (so it sticks
   * across devices); this keeps the local session + the on-login language
   * sync consistent so a reload doesn't revert the page to a stale locale.
   */
  setUserLanguage: (this: void, language: 'de' | 'en') => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<InMemorySession | null>(() => {
    const persisted = readPersistedSession();
    return persisted ? { accessToken: persisted.legacyToken, user: persisted.user } : null;
  });

  /* Persist user-only on change (no tokens — see PersistedSession). */
  useEffect(() => {
    writePersistedSession(session ? { user: session.user } : null);
  }, [session]);

  /* Silent refresh failure → clear session so route guards redirect to
   * /login. The api-client tries POST /auth/refresh on every 401; only
   * when THAT also fails does it call the session-lost handler, so by
   * the time this fires the user genuinely has no usable session. */
  useEffect(() => {
    return api.registerSessionLostHandler(() => {
      setSession(null);
    });
  }, []);

  const extractToken = (response: api.LoginSession): string => {
    // Server may return tokens at the top level (legacy fixture shape)
    // or under `tokens` (current shape). In cookie mode neither is
    // present and we deliberately operate without a token in JS.
    return response.tokens?.accessToken ?? response.accessToken ?? '';
  };

  const adoptSession = useCallback(
    async (response: api.LoginSession, email: string): Promise<void> => {
      const tokenForCalls = extractToken(response);
      // Merge the login payload's user with the email we already had —
      // in cookie mode the server's user payload may omit email since
      // identity is established by the cookie + /users/me round-trip.
      let user: api.UserProfile = response.user
        ? { ...response.user, email: response.user.email ?? email }
        : { id: 'unknown', email, role: 'client' };
      try {
        user = await api.fetchProfile(tokenForCalls);
      } catch (cause) {
        // CodeFix-V1 #50 — log but don't surface; the login payload's
        // basic user lets the UI render. Logging makes it possible to
        // catch /users/me regressions during dev.
        console.warn('adoptSession: fetchProfile failed, using login payload user', cause);
      }
      setSession({ accessToken: tokenForCalls, user });
      // Stamp the session start so the "stay logged in for N days"
      // setting can enforce a client-side cap. Silent refreshes do
      // NOT restamp this (they extend access-token freshness, not the
      // session-lifetime window).
      markSessionStarted();
    },
    [],
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      const response = await api.login(email, password);
      if (api.isMfaRequired(response)) {
        return { kind: 'mfa_required', mfaToken: response.mfa_token };
      }
      await adoptSession(response, email);
      return { kind: 'session' };
    },
    [adoptSession],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string): Promise<void> => {
      const response = await api.verifyTwoFactorLogin(mfaToken, code);
      // The session response includes user.email; use it for the fallback profile.
      const email = response.user?.email ?? '';
      await adoptSession(response, email);
    },
    [adoptSession],
  );

  /**
   * #8/#9 — on-login + on-boot language sync. Whenever the signed-in
   * user's saved language changes (login fetches it from /users/me; a
   * cold boot restores it from the persisted session; settings updates it
   * via setUserLanguage), apply it to the page so tools + UI render in the
   * client's chosen language. Logged-out flows (user null) are untouched,
   * so the public language detector / register-page toggle still win there.
   */
  useEffect(() => {
    const lang = session?.user?.language;
    if (lang === 'de' || lang === 'en') {
      void setLocale(lang);
    }
  }, [session?.user?.language]);

  const setUserLanguage = useCallback((language: 'de' | 'en'): void => {
    setSession((prev) =>
      prev ? { ...prev, user: { ...prev.user, language } } : prev,
    );
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    if (session) {
      // Best-effort: don't block sign-out on a server-side failure.
      try {
        await api.logout(session.accessToken);
      } catch {
        /* swallow */
      }
    }
    setSession(null);
    clearSessionStarted();
  }, [session]);

  /* Periodic "stay logged in for N days" check. If the user picked
   * 7 / 14 / 30 days in their settings and that window has elapsed
   * since their last LOGIN (silent refreshes don't restart it),
   * force a sign-out. Runs every minute while signed in. */
  useEffect(() => {
    if (!session) return;
    const tick = (): void => {
      if (isSessionExpired()) {
        setSession(null);
        clearSessionStarted();
      }
    };
    tick(); // check immediately on mount / session change
    const interval = window.setInterval(tick, 60 * 1000);
    return () => {
      window.clearInterval(interval);
    };
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      // Returns '' when in cookie-only mode. Callers that pass this to
      // apiRequest will have no Authorization header added (the if
      // (accessToken) check in api-client gates the header); auth flows
      // through HttpOnly cookies instead.
      accessToken: session?.accessToken ?? '',
      signIn,
      verifyMfa,
      signOut,
      setUserLanguage,
    }),
    [session, signIn, verifyMfa, signOut, setUserLanguage],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be called inside <AuthProvider>');
  }
  return ctx;
}
